import type { ExportCell, ExportTable } from './csv'
import { zip } from '../lib/zip'

/**
 * The creatives table as an `.xlsx` workbook (GRO-81 design, D2).
 *
 * A CSV holds a figure as text, and a spreadsheet whose region uses a decimal comma reads
 * `1510.00` as text (GRO-64, task 18.1). A workbook holds it as a number, and every
 * spreadsheet shows it with its own separator, so the file needs no guess at the reader's
 * locale. Marked figures stay text, as in the CSV: they must not be summed.
 *
 * The workbook is the minimal SpreadsheetML package: one sheet, inline strings, four cell
 * formats. `toXlsx` returns the finished bytes; the screen only hands them over.
 */

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const MAIN_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main'
const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
const PKG_REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships'
const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'

/** Indexes into `cellXfs` below. */
const STYLE_HEADER = 4
const STYLE_FOR_DECIMALS: Record<number, number> = { 0: 1, 1: 2, 2: 3 }

function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/**
 * A string as SpreadsheetML stores it, in three steps whose order matters.
 *
 * Excel reads `_xHHHH_` in a string as the character with that code, so a name holding
 * that literal text would change on open: its underscore is escaped as `_x005F_`. A
 * carriage return is written `_x000D_`, which XML parsing would otherwise turn into a line
 * feed. Then the characters XML 1.0 forbids are dropped, and the rest is XML-escaped.
 */
export function spreadsheetText(value: string): string {
  const encoded = value.replace(/_x([0-9A-Fa-f]{4})_/g, '_x005F_x$1_').replace(/\r/g, '_x000D_')
  const legal = encoded.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '')
  return escapeXml(legal)
}

function columnName(index: number): string {
  let name = ''
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) name = String.fromCharCode(65 + ((n - 1) % 26)) + name
  return name
}

function textXml(ref: string, text: string, style?: number): string {
  const s = style === undefined ? '' : ` s="${style}"`
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${spreadsheetText(text)}</t></is></c>`
}

function cellXml(cell: ExportCell, ref: string): string {
  switch (cell.kind) {
    case 'number':
      return `<c r="${ref}" s="${STYLE_FOR_DECIMALS[cell.decimals] ?? 0}"><v>${cell.value}</v></c>`
    case 'text':
      return textXml(ref, cell.text)
    case 'empty':
      return ''
  }
}

export function sheetXml(table: ExportTable): string {
  const head = `<row r="1">${table.header.map((name, col) => textXml(`${columnName(col)}1`, name, STYLE_HEADER)).join('')}</row>`
  const rows = table.records.map((record, index) => {
    const r = index + 2
    return `<row r="${r}">${record.map((cell, col) => cellXml(cell, `${columnName(col)}${r}`)).join('')}</row>`
  })
  return (
    XML_HEAD +
    `<worksheet xmlns="${MAIN_NS}">` +
    '<sheetViews><sheetView workbookViewId="0">' +
    '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
    '</sheetView></sheetViews>' +
    `<sheetData>${head}${rows.join('')}</sheetData>` +
    '</worksheet>'
  )
}

const CONTENT_TYPES =
  XML_HEAD +
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
  '</Types>'

const ROOT_RELS =
  XML_HEAD +
  `<Relationships xmlns="${PKG_REL_NS}">` +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  '</Relationships>'

const WORKBOOK_RELS =
  XML_HEAD +
  `<Relationships xmlns="${PKG_REL_NS}">` +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
  '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
  '</Relationships>'

/** Formats 0-4: general, `0`, `0.0`, `0.00`, and the bold header. */
const STYLES =
  XML_HEAD +
  `<styleSheet xmlns="${MAIN_NS}">` +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="0.0"/></numFmts>' +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="5">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="1" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '<xf numFmtId="2" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '</styleSheet>'

/** Excel refuses a sheet name over 31 characters or holding any of `[]:*?/\`. */
function sheetName(name: string): string {
  return escapeXml(name.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31) || 'Sheet1')
}

export function toXlsx(table: ExportTable, name: string): Uint8Array<ArrayBuffer> {
  const workbook =
    XML_HEAD +
    `<workbook xmlns="${MAIN_NS}" xmlns:r="${REL_NS}">` +
    `<sheets><sheet name="${sheetName(name)}" sheetId="1" r:id="rId1"/></sheets>` +
    '</workbook>'
  return zip([
    { name: '[Content_Types].xml', data: CONTENT_TYPES },
    { name: '_rels/.rels', data: ROOT_RELS },
    { name: 'xl/workbook.xml', data: workbook },
    { name: 'xl/_rels/workbook.xml.rels', data: WORKBOOK_RELS },
    { name: 'xl/styles.xml', data: STYLES },
    { name: 'xl/worksheets/sheet1.xml', data: sheetXml(table) },
  ])
}
